import { Component, Inject, ChangeDetectionStrategy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { OutputsCollectionsService } from 'src/app/services/outputs-collections.service';
import { AuthService } from 'src/app/services/auth.service';
import { Permission } from 'src/app/constants/permission.constant';
import {
  AssetPreviewResult,
  CollectionAssetPreviewResult,
} from 'src/app/models/outputs-collections/outputs-collections-result';
import { ReviewProductDataFilenamesDialogData } from 'src/app/models/outputs-collections/review-product-data-filenames/dialog-data';
import { ReviewProductDataFilenamesResult } from 'src/app/models/outputs-collections/review-product-data-filenames/review-product-data-filenames-result';
import { ReviewProductDataFilenamesRow } from 'src/app/models/outputs-collections/review-product-data-filenames/review-product-data-filenames-row';
import { ReviewProductDataFilenamesSummary } from 'src/app/models/outputs-collections/review-product-data-filenames/review-product-data-filenames-summary';
import { ProductDataFilenameOrigin } from 'src/app/models/outputs-collections/review-product-data-filenames/product-data-filename-origin';
import { CannotProceedProductRow } from 'src/app/models/outputs-collections/shared/cannot-proceed-product-row';
import { FileExtension } from 'src/app/models/product-data-filename-conventions/file-extension';
import {
  FileExtension as PdfcFileExtension,
  ProductDataFilenameConvention,
} from 'src/app/models/product-data-filename-conventions/product-data-filename-convention';
import {
  UpdateProductDataFilenameConventionDialogComponent,
  UpdateProductDataFilenameConventionDialogData,
} from '../../organizations/product-data-filename-conventions/update-product-data-filename-convention-dialog.component';

@Component({
  selector: 'app-review-product-data-filenames-dialog',
  templateUrl: './review-product-data-filenames-dialog.component.html',
  styleUrls: ['./review-product-data-filenames-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class ReviewProductDataFilenamesDialogComponent implements OnInit {
  isLoading = true;
  hasErrored = false;
  result: ReviewProductDataFilenamesResult | null = null;
  canEditPdfc = false;
  readonly displayedColumns: string[] = [
    'product',
    'format',
    'generatedFilename',
    'conventionCurrent',
  ];
  readonly cannotProceedColumns: string[] = ['product', 'reason'];

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly data: ReviewProductDataFilenamesDialogData,
    public readonly dialogRef: MatDialogRef<ReviewProductDataFilenamesDialogComponent>,
    private readonly outputsCollectionsService: OutputsCollectionsService,
    private readonly dialog: MatDialog,
    private readonly authService: AuthService,
  ) {}

  ngOnInit(): void {
    this.canEditPdfc = this.authService.hasPermission(
      Permission.ProductDataFilenameConventionsFull,
    );
    this.loadPreview();
  }

  openEditPdfc(): void {
    const dialogData: UpdateProductDataFilenameConventionDialogData = {
      organizationId: this.data.scope.organizationId,
      current: this.buildCurrentConvention(),
    };
    const dialogRef = this.dialog.open(UpdateProductDataFilenameConventionDialogComponent, {
      data: dialogData,
    });
    dialogRef
      .afterClosed()
      .subscribe((saved: ProductDataFilenameConvention | undefined) => {
        if (saved == null) return;
        this.loadPreview();
      });
  }

  // Update dialog reads only `filename` to seed its form; other fields are
  // required by the type but unused pre-save (append-only write path).
  private buildCurrentConvention(): ProductDataFilenameConvention {
    return {
      id: '',
      organizationId: this.data.scope.organizationId,
      createdAt: '',
      filename: {
        identifier: this.data.convention.identifier.kind,
        extensions: this.data.convention.extensions.enabled as unknown as PdfcFileExtension[],
      },
      writeReason: { kind: 'conventionChange' },
    };
  }

  loadPreview(): void {
    this.outputsCollectionsService
      .previewOutputsCollection({
        productIds: this.data.scope.productIds,
        outputFormats: [],
        productDataFormats: this.data.productDataFormats,
      })
      .subscribe({
        next: (response: CollectionAssetPreviewResult) => {
          this.result = this.mapToProductDataFilenamesResult(response);
          this.isLoading = false;
        },
        error: () => {
          this.result = null;
          this.hasErrored = true;
          this.isLoading = false;
        },
      });
  }

  // templateCurrent stays `true` uniformly and cannotProceed[] stays empty
  // until the backend response emits (a) an outdated-filename flag and
  // (b) a per-product cannot-proceed reason field (follow-ups: BE isOutdated,
  // BE cannotProceed reason).
  mapToProductDataFilenamesResult(
    response: CollectionAssetPreviewResult,
  ): ReviewProductDataFilenamesResult {
    const productDataAssets = response.assets.filter((asset) => asset.isProductData);
    const rows = productDataAssets.map((asset) => this.mapToRow(asset));
    const cannotProceed: CannotProceedProductRow[] = [];
    const summary = this.buildSummary(rows, cannotProceed);
    return {
      convention: this.data.convention,
      rows,
      cannotProceed,
      summary,
    };
  }

  getFileName(key: string): string {
    const parts = key.split('/');
    return parts[parts.length - 1];
  }

  private mapToRow(asset: AssetPreviewResult): ReviewProductDataFilenamesRow {
    const origin: ProductDataFilenameOrigin = {
      product: {
        id: asset.productId,
        identifiers: {},
      },
      format: {
        extension: this.parseFileExtension(asset.s3Key),
      },
    };
    return {
      origin,
      filename: {
        generated: this.getFileName(asset.s3Key),
        templateCurrent: true,
      },
    };
  }

  private parseFileExtension(s3Key: string): FileExtension {
    const dotIndex = s3Key.lastIndexOf('.');
    const extensionText = dotIndex >= 0 ? s3Key.substring(dotIndex + 1).toLowerCase() : '';
    if (extensionText === 'csv') return FileExtension.Csv;
    return FileExtension.Json;
  }

  private buildSummary(
    rows: ReviewProductDataFilenamesRow[],
    cannotProceed: CannotProceedProductRow[],
  ): ReviewProductDataFilenamesSummary {
    return {
      totalFilenames: rows.length,
      conventionMismatchCount: rows.filter((row) => !row.filename.templateCurrent).length,
      cannotProceedCount: cannotProceed.length,
    };
  }
}
